/**
 * Organization Detail Page
 *
 * Drill-down view for a single organization.
 * Shows subscription, metrics, groups, and recent activity.
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { founderDashboardAPI } from '../../services/api';
import { useEnvironment } from '../../context/EnvironmentContext';
import KPICard from '../../components/founder-dashboard/KPICard';
import DataTable from '../../components/founder-dashboard/DataTable';
import HealthBadge from '../../components/founder-dashboard/HealthBadge';
import RefreshIndicator from '../../components/founder-dashboard/RefreshIndicator';
import FeatureHealthCard from '../../components/feature-analytics/FeatureHealthCard';
import {
  ArrowLeftIcon,
  BuildingOfficeIcon,
  GlobeAltIcon,
  CalendarIcon,
} from '@heroicons/react/24/outline';

export default function OrganizationDetailPage() {
  const { currentEnv } = useEnvironment();
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dataEnv, setDataEnv] = useState(null); // Track which env the data/error belongs to
  const [featureHealth, setFeatureHealth] = useState(null);
  const [featureHealthLoading, setFeatureHealthLoading] = useState(true);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await founderDashboardAPI.getOrganization(id);
      setData(response.data);
      setDataEnv(currentEnv);
    } catch (err) {
      console.error('[OrganizationDetailPage] Error:', err);
      const errorData = err.response?.data?.error;
      const errorMsg = typeof errorData === 'string' ? errorData :
                       errorData?.message || err.message || 'Failed to load data';
      setError(errorMsg);
      setDataEnv(currentEnv);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Clear all state and refetch when environment changes
    setData(null);
    setError(null);
    setFeatureHealth(null);
    fetchData();
  }, [id, currentEnv]);

  // Fetch feature health data when org id is available
  useEffect(() => {
    const loadFeatureHealth = async () => {
      if (!id) return;
      setFeatureHealthLoading(true);
      try {
        const response = await founderDashboardAPI.getOrgFeatureHealth(id);
        setFeatureHealth(response.data.features);
      } catch (err) {
        console.error('[OrganizationDetailPage] Failed to load feature health:', err);
        // Don't set error state - just log and continue, feature health is supplemental
      } finally {
        setFeatureHealthLoading(false);
      }
    };
    loadFeatureHealth();
  }, [id, currentEnv]);

  const groupColumns = [
    {
      key: 'name',
      header: 'Group Name',
      sortable: true,
      render: (value, row) => (
        <div>
          <p className="font-medium text-gray-900">{value}</p>
          <p className="text-xs text-gray-500 capitalize">{row.type}</p>
        </div>
      ),
    },
    {
      key: 'memberCount',
      header: 'Members',
      sortable: true,
    },
    {
      key: 'meetingDay',
      header: 'Meeting Day',
      render: (value) => value || '-',
    },
    {
      key: 'healthScore',
      header: 'Health',
      sortable: true,
      render: (value) => <HealthBadge score={value} showLabel={false} />,
    },
    {
      key: 'lastEventAt',
      header: 'Last Event',
      render: (value) => {
        if (!value) return <span className="text-gray-400">Never</span>;
        return new Date(value).toLocaleDateString();
      },
    },
  ];

  // Only show error if it's from the current environment
  if (error && dataEnv === currentEnv) {
    return (
      <div className="space-y-4">
        <button
          onClick={() => navigate('/founder-dashboard/organizations')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
        >
          <ArrowLeftIcon className="w-5 h-5" />
          Back to Organizations
        </button>
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <p className="text-red-800 font-medium">Error loading organization</p>
          <p className="text-red-600 text-sm mt-1">{error}</p>
          <button
            onClick={fetchData}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const org = data?.organization || {};
  const metrics = data?.metrics || {};
  const groups = data?.groups || [];
  const recentActivity = data?.recentActivity || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <button
            onClick={() => navigate('/founder-dashboard/organizations')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-2"
          >
            <ArrowLeftIcon className="w-4 h-4" />
            Back to Organizations
          </button>
          {loading ? (
            <div className="h-8 w-48 bg-gray-200 rounded animate-pulse"></div>
          ) : (
            <h2 className="text-2xl font-bold text-gray-900">{org.name}</h2>
          )}
          {org.location && (
            <p className="text-gray-500 mt-1">{org.location}</p>
          )}
        </div>
        <RefreshIndicator onRefresh={fetchData} loading={loading} />
      </div>

      {/* Organization Info Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div>
            <p className="text-sm text-gray-500">Tier</p>
            <p className="text-lg font-semibold text-gray-900 capitalize mt-1">
              {loading ? '-' : org.tierName || org.tier || 'Trial'}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Subscription Status</p>
            <p className="mt-1">
              {loading ? (
                '-'
              ) : (
                <span
                  className={`px-2 py-1 rounded-full text-xs font-medium ${
                    org.subscription?.status === 'active'
                      ? 'bg-green-100 text-green-800'
                      : org.subscription?.status === 'trialing'
                      ? 'bg-blue-100 text-blue-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}
                >
                  {org.subscription?.status || 'Unknown'}
                </span>
              )}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-500">MRR</p>
            <p className="text-lg font-semibold text-gray-900 mt-1">
              {loading
                ? '-'
                : new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'USD',
                    minimumFractionDigits: 0,
                  }).format(org.subscription?.mrr || 0)}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Created</p>
            <p className="text-lg font-semibold text-gray-900 mt-1">
              {loading ? '-' : new Date(org.createdAt).toLocaleDateString()}
            </p>
          </div>
        </div>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <KPICard title="Members" value={metrics.memberCount} loading={loading} />
        <KPICard title="Groups" value={metrics.groupCount} loading={loading} />
        <KPICard title="Prayers This Month" value={metrics.prayersThisMonth} loading={loading} />
        <KPICard title="Events This Month" value={metrics.eventsThisMonth} loading={loading} />
        <KPICard
          title="Avg Attendance"
          value={metrics.averageAttendanceRate}
          format="percent"
          loading={loading}
        />
      </div>

      {/* Feature Health Section */}
      <div>
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Feature Health</h3>

        {featureHealthLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-36 bg-slate-100 rounded-xl animate-pulse" />
            ))}
          </div>
        ) : featureHealth ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <FeatureHealthCard
              feature="prayer"
              health={featureHealth.prayer?.health || 'yellow'}
              adoption={featureHealth.prayer?.adoption || 0}
              usageLabel={featureHealth.prayer?.usageLabel || '0/user'}
              effectiveness={featureHealth.prayer?.effectiveness || 0}
              organizationId={id}
            />
            <FeatureHealthCard
              feature="attendance"
              health={featureHealth.attendance?.health || 'yellow'}
              adoption={featureHealth.attendance?.adoption || 0}
              usageLabel={featureHealth.attendance?.usageLabel || '0%'}
              effectivenessLabel={featureHealth.attendance?.effectivenessLabel || '0 at-risk'}
              organizationId={id}
            />
            <FeatureHealthCard
              feature="events"
              health={featureHealth.events?.health || 'yellow'}
              adoption={featureHealth.events?.adoption || 0}
              usageLabel={featureHealth.events?.usageLabel || '0/group'}
              effectiveness={featureHealth.events?.effectiveness || 0}
              organizationId={id}
            />
            <FeatureHealthCard
              feature="chat"
              health={featureHealth.chat?.health || 'yellow'}
              adoption={featureHealth.chat?.adoption || 0}
              usageLabel={featureHealth.chat?.usageLabel || '0/day'}
              effectiveness={featureHealth.chat?.effectiveness || 0}
              organizationId={id}
            />
          </div>
        ) : (
          <p className="text-gray-500 text-sm">Feature health data unavailable</p>
        )}
      </div>

      {/* Groups Table */}
      <div>
        <h3 className="text-lg font-semibold text-gray-800 mb-4">
          Groups ({groups.length})
        </h3>
        <DataTable
          data={groups}
          columns={groupColumns}
          loading={loading}
          onRowClick={(group) => navigate(`/founder-dashboard/groups/${group.id}`)}
          emptyMessage="No groups found"
        />
      </div>

      {/* Recent Activity */}
      {recentActivity.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Recent Activity</h3>
          <div className="space-y-3">
            {recentActivity.slice(0, 10).map((activity, index) => (
              <div
                key={index}
                className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0"
              >
                <div className="flex items-center gap-3">
                  <CalendarIcon className="w-4 h-4 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-900 capitalize">
                      {activity.type?.replace(/_/g, ' ')}
                    </p>
                    {activity.groupName && (
                      <p className="text-xs text-gray-500">{activity.groupName}</p>
                    )}
                  </div>
                </div>
                <span className="text-xs text-gray-400">
                  {new Date(activity.timestamp).toLocaleDateString()}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
